// src/pages/Admin/DossierInscription/useDossiersInscriptionAdmin.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  deleteDossierInscription,
  ensureAdherentFolder,
  getAllDossiersInscription,
  hasAdherentFolder,
  updateDossierInscription,
} from "../../../../services/dossierServices";
import { getSettings } from "../../../../services/settingsServices";
import { getGroupsList } from "../../../../services/groupServices";
import { sendNotificationToAdmin } from "../../../../services/notificationServices";

import {
  CHUNK,
  extractArray,
  getAdherentId,
  getAdherentName,
  getDossierCompletion,
  getDossierId,
  getGroupsText,
  neighborsFromSeason,
  normalizeText,
  runWithConcurrency,
  todayISO,
} from "./dossier.helpers";

export default function useDossiersInscriptionAdmin(currentUser = null) {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");
  const [groups, setGroups] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [seasonFilter, setSeasonFilter] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");

  const [validateCmFilter, setValidateCmFilter] = useState("");
  const [validateLicenceFilter, setValidateLicenceFilter] = useState("");
  const [validateReglementFilter, setValidateReglementFilter] = useState("");

  const [sortField, setSortField] = useState("name"); // name | completion
  const [sortOrder, setSortOrder] = useState("asc");

  const [visibleCount, setVisibleCount] = useState(CHUNK);
  const scrollRef = useRef(null);

  const [fsFlags, setFsFlags] = useState({});
  const fetchedFsRef = useRef(new Set());
  const fetchingFsRef = useRef(new Set());

  const [rowRefreshing, setRowRefreshing] = useState({});
  const [folderCreating, setFolderCreating] = useState({});

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(normalizeText(searchTerm));
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const checkForDuplicates = useCallback((rows) => {
    const seen = new Set();
    const duplicates = new Set();

    rows.forEach((dossier) => {
      const key = normalizeText(getAdherentName(dossier));

      if (!key) return;

      if (seen.has(key)) {
        duplicates.add(getAdherentName(dossier));
      }

      seen.add(key);
    });

    return duplicates.size
      ? `Attention, doublon détecté : ${Array.from(duplicates).join(", ")}`
      : null;
  }, []);

  const [duplicateWarning, setDuplicateWarning] = useState(null);

  const normalizeDossier = (dossier) => ({
    ...dossier,
    Adherent: {
      ...(dossier?.Adherent || {}),
      Groups: Array.isArray(dossier?.Adherent?.Groups)
        ? dossier.Adherent.Groups
        : [],
    },
    validate_cm: Boolean(dossier?.validate_cm),
    validate_licence: Boolean(dossier?.validate_licence),
    validate_reglement: Boolean(dossier?.validate_reglement),
    date_cm: dossier?.date_cm || null,
  });

  const fetchDossiers = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getAllDossiersInscription();
      const rows = extractArray(response).map(normalizeDossier);

      setDossiers(rows);
      setDuplicateWarning(checkForDuplicates(rows));
    } catch (error) {
      console.error("Erreur chargement dossiers :", error);
      toast.error("Erreur lors du chargement des dossiers d'inscription.");
    } finally {
      setLoading(false);
    }
  }, [checkForDuplicates]);

  const fetchDossiersSilent = useCallback(async () => {
    try {
      const response = await getAllDossiersInscription();
      const rows = extractArray(response).map(normalizeDossier);

      setDossiers(rows);
      setDuplicateWarning(checkForDuplicates(rows));
    } catch (error) {
      console.error("Erreur rechargement dossiers :", error);
      toast.error("Erreur lors du rechargement des dossiers.");
    }
  }, [checkForDuplicates]);

  const fetchContext = useCallback(async () => {
    try {
      const [settingsResponse, groupsResponse] = await Promise.all([
        getSettings(),
        getGroupsList(),
      ]);

      const settingsRows = extractArray(settingsResponse);
      const active = settingsRows?.[0]?.saison_active || "";

      setActiveSeason(active);
      setSeasonFilter(active || "");
      setSeasons(neighborsFromSeason(active));
      setGroups(extractArray(groupsResponse));
    } catch (error) {
      console.error("Erreur contexte dossiers :", error);
      toast.error("Erreur lors du chargement des paramètres.");
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    await Promise.all([fetchDossiers(), fetchContext()]);
  }, [fetchDossiers, fetchContext]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const fetchFsFlagsFor = useCallback(async (adherentIds = []) => {
    const uniqueIds = Array.from(
      new Set(adherentIds.filter(Boolean).map(String)),
    );

    const todo = uniqueIds.filter(
      (id) => !fetchedFsRef.current.has(id) && !fetchingFsRef.current.has(id),
    );

    if (!todo.length) return;

    todo.forEach((id) => fetchingFsRef.current.add(id));

    await runWithConcurrency(
      todo,
      async (id) => {
        try {
          const response = await hasAdherentFolder(id);

          setFsFlags((prev) => ({
            ...prev,
            [id]: Boolean(response?.exists),
          }));
        } catch {
          setFsFlags((prev) => ({
            ...prev,
            [id]: false,
          }));
        } finally {
          fetchingFsRef.current.delete(id);
          fetchedFsRef.current.add(id);
        }
      },
      6,
    );
  }, []);

  const filtered = useMemo(() => {
    const rows = dossiers.filter((dossier) => {
      const adherent = dossier?.Adherent || {};

      const haystack = normalizeText(
        [
          adherent?.nom,
          adherent?.prenom,
          adherent?.email,
          adherent?.licence_number,
          adherent?.commune,
          adherent?.saison_active,
          dossier?.cm_url,
          dossier?.licence_url,
          dossier?.lien_drive,
          getGroupsText(dossier),
        ]
          .filter(Boolean)
          .join(" "),
      );

      const matchesSearch =
        !debouncedSearch || haystack.includes(debouncedSearch);

      const matchesSeason =
        !seasonFilter ||
        String(adherent?.saison_active || "") === String(seasonFilter);

      const matchesGroup =
        !selectedGroup ||
        (Array.isArray(adherent?.Groups) &&
          adherent.Groups.some(
            (group) => String(group?.id) === String(selectedGroup),
          ));

      const matchesCm =
        !validateCmFilter ||
        String(Boolean(dossier?.validate_cm)) === String(validateCmFilter);

      const matchesLicence =
        !validateLicenceFilter ||
        String(Boolean(dossier?.validate_licence)) ===
          String(validateLicenceFilter);

      const matchesReglement =
        !validateReglementFilter ||
        String(Boolean(dossier?.validate_reglement)) ===
          String(validateReglementFilter);

      return (
        matchesSearch &&
        matchesSeason &&
        matchesGroup &&
        matchesCm &&
        matchesLicence &&
        matchesReglement
      );
    });

    rows.sort((a, b) => {
      if (sortField === "completion") {
        const ca = getDossierCompletion(a).percent;
        const cb = getDossierCompletion(b).percent;

        return sortOrder === "asc" ? ca - cb : cb - ca;
      }

      const nameA = getAdherentName(a);
      const nameB = getAdherentName(b);

      return sortOrder === "asc"
        ? nameA.localeCompare(nameB, "fr", { sensitivity: "base" })
        : nameB.localeCompare(nameA, "fr", { sensitivity: "base" });
    });

    return rows;
  }, [
    dossiers,
    debouncedSearch,
    seasonFilter,
    selectedGroup,
    validateCmFilter,
    validateLicenceFilter,
    validateReglementFilter,
    sortField,
    sortOrder,
  ]);

  const visibleDossiers = filtered.slice(0, visibleCount);

  useEffect(() => {
    const ids = visibleDossiers.map(getAdherentId).filter(Boolean);
    fetchFsFlagsFor(ids);
  }, [visibleDossiers, fetchFsFlagsFor]);

  useEffect(() => {
    setVisibleCount(CHUNK);

    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [
    debouncedSearch,
    seasonFilter,
    selectedGroup,
    validateCmFilter,
    validateLicenceFilter,
    validateReglementFilter,
    sortField,
    sortOrder,
  ]);

  const stats = useMemo(() => {
    const base = filtered;

    return {
      total: dossiers.length,
      affiches: base.length,
      complets: base.filter((dossier) => getDossierCompletion(dossier).complete)
        .length,
      cmOk: base.filter((dossier) => dossier?.validate_cm).length,
      licenceOk: base.filter((dossier) => dossier?.validate_licence).length,
      reglementOk: base.filter((dossier) => dossier?.validate_reglement).length,
    };
  }, [dossiers, filtered]);

  const onScroll = (event) => {
    const element = event.currentTarget;

    if (
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - 220
    ) {
      setVisibleCount((current) =>
        current >= filtered.length
          ? current
          : Math.min(current + CHUNK, filtered.length),
      );
    }
  };

  const setRowRefreshingFlag = (id, enabled) => {
    setRowRefreshing((prev) => ({
      ...prev,
      [id]: Boolean(enabled),
    }));
  };

  const isRowRefreshing = (id) => Boolean(rowRefreshing[id]);

  const getAdminName = (userInfo) => {
    const prenom = String(userInfo?.prenom || "").trim();
    const nom = String(userInfo?.nom || "")
      .trim()
      .toUpperCase();

    return `${prenom} ${nom}`.trim() || "un administrateur";
  };

  const getTodayFr = () => {
    return new Date().toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getValidationNotificationMessage = (dossier, field) => {
    const adherentName = getAdherentName(dossier);
    const date = getTodayFr();
    const adminName = getAdminName(currentUser);

    if (field === "validate_licence") {
      return `Licence de ${adherentName} validée le ${date} par ${adminName}.`;
    }

    if (field === "validate_reglement") {
      return `Règlement de ${adherentName} validé le ${date} par ${adminName}.`;
    }

    return "";
  };

  const notifyAdminValidation = async (dossier, field) => {
    if (!["validate_licence", "validate_reglement"].includes(field)) return;

    const token = currentUser?.token || localStorage.getItem("token");
    const message = getValidationNotificationMessage(dossier, field);

    if (!token || !message) {
      console.warn(
        "Notification admin non envoyée : token ou message manquant.",
      );
      return;
    }

    try {
      await sendNotificationToAdmin(message, token);
    } catch (error) {
      console.error("Erreur notification admin validation dossier :", error);
    }
  };

  const toggleValidate = async (dossier, field, label) => {
    const id = getDossierId(dossier);

    if (!id) return;

    const next = !dossier[field];

    const payload = {
      [field]: next,
    };

    if (field === "validate_cm") {
      if (next) {
        payload.date_cm = dossier?.date_cm || todayISO();
      } else {
        payload.date_cm = null;
      }
    }

    try {
      setRowRefreshingFlag(id, true);

      await updateDossierInscription(id, payload);

      if (next && ["validate_licence", "validate_reglement"].includes(field)) {
        await notifyAdminValidation(dossier, field);
      }

      await fetchDossiersSilent();

      toast.success(`${label} mis à jour (${next ? "Oui" : "Non"}).`);
    } catch (error) {
      console.error(`Erreur mise à jour ${label} :`, error);
      toast.error(`Impossible de mettre à jour ${label}.`);
    } finally {
      setRowRefreshingFlag(id, false);
    }
  };

  const handleEnsureFolder = async (adherentId) => {
    if (!adherentId) return;

    try {
      setFolderCreating((prev) => ({ ...prev, [adherentId]: true }));

      const response = await ensureAdherentFolder(adherentId);

      if (response?.created) {
        toast.success("Dossier serveur créé.");
      } else {
        toast.info("Le dossier serveur existait déjà.");
      }

      setFsFlags((prev) => ({
        ...prev,
        [String(adherentId)]: true,
      }));
    } catch (error) {
      console.error("Erreur création dossier serveur :", error);
      toast.error("Erreur lors de la création du dossier serveur.");
    } finally {
      setFolderCreating((prev) => ({ ...prev, [adherentId]: false }));
    }
  };

  const deleteDossier = async (id) => {
    if (!window.confirm("Supprimer ce dossier d'inscription ?")) return;

    try {
      await deleteDossierInscription(id);
      toast.success("Dossier supprimé avec succès.");
      await fetchDossiers();
    } catch (error) {
      console.error("Erreur suppression dossier :", error);
      toast.error("Erreur lors de la suppression du dossier.");
    }
  };

  const toggleNameSort = () => {
    setSortField("name");
    setSortOrder((current) =>
      sortField === "name" && current === "asc" ? "desc" : "asc",
    );
  };

  const toggleCompletionSort = () => {
    setSortField("completion");
    setSortOrder((current) =>
      sortField === "completion" && current === "asc" ? "desc" : "asc",
    );
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSeasonFilter(activeSeason || "");
    setSelectedGroup("");
    setValidateCmFilter("");
    setValidateLicenceFilter("");
    setValidateReglementFilter("");
    setSortField("name");
    setSortOrder("asc");
  };

  return {
    state: {
      dossiers,
      loading,
      seasons,
      activeSeason,
      groups,
      searchTerm,
      seasonFilter,
      selectedGroup,
      validateCmFilter,
      validateLicenceFilter,
      validateReglementFilter,
      sortField,
      sortOrder,
      visibleCount,
      scrollRef,
      filtered,
      visibleDossiers,
      fsFlags,
      rowRefreshing,
      folderCreating,
      duplicateWarning,
      stats,
    },

    setters: {
      setSearchTerm,
      setSeasonFilter,
      setSelectedGroup,
      setValidateCmFilter,
      setValidateLicenceFilter,
      setValidateReglementFilter,
    },

    handlers: {
      fetchDossiers,
      fetchInitialData,
      onScroll,
      toggleValidate,
      handleEnsureFolder,
      deleteDossier,
      isRowRefreshing,
      toggleNameSort,
      toggleCompletionSort,
      resetFilters,
    },
  };
}
